/**
 * MatchExplainerModal — visual breakdown of why a creator scored what they
 * scored against the current brand. Powered by Digital DNA v2 (the SSP's
 * hybrid semantic + lexical pipeline).
 *
 * Triggered from Discover Creators result cards. Pulls /api/brand/dna/explain
 * which returns:
 *   {
 *     dna_score, raw_match,
 *     components: [{label, score, raw, weight, note}, ...],
 *     bonuses:    [{label, score, weight, note}, ...],
 *     penalties:  [{label, score, weight, note}, ...],
 *     summary:    [string, ...],
 *     semantic_used, semantic_score, semantic_blend_alpha,
 *     vertical
 *   }
 *
 * Why the modal exists: a single match score (e.g. "23.2%") is opaque. The
 * brand can't tell if it's a strong-tone-but-weak-audience pick, a weak-content
 * but strong-affinity pick, etc. The breakdown turns the score into a defensible
 * decision.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { explainMatch } from '../../services/dsp';
import {
  Loader2, Sparkles, Award, AlertTriangle, ChevronRight, Info,
} from 'lucide-react';
import FirstPartyDataPanel from './FirstPartyDataPanel';

const CARD = '#121212';
const BORDER = '#1E1E1E';
const T = '#00F5FF';
const P = '#FF6BBD';
const GREEN = '#4ade80';
const AMBER = '#FFC83D';
const RED = '#FF6B6B';

// ─────────────── helpers ───────────────

function pct(raw) {
  if (raw == null) return '—';
  return `${Math.round(raw * 100)}%`;
}

function scoreColor(raw) {
  if (raw >= 0.7) return GREEN;
  if (raw >= 0.4) return T;
  if (raw >= 0.2) return AMBER;
  return '#52525b';  // gray-600
}

// ─────────────── ComponentBar ───────────────
// One row per match component (Audience overlap, Tone match, etc.).
// The Semantic match row gets a subtle gradient stroke so it pops as the v2 upgrade.

function ComponentBar({ label, raw, score, weight, note, isSemantic }) {
  const color = scoreColor(raw || 0);
  const pctVal = Math.max(0, Math.min(1, raw || 0));
  const accent = isSemantic ? T : color;

  return (
    <div
      className="rounded-lg p-3 transition-all"
      data-testid={`mx-component-${(label || '').toLowerCase().replace(/\s+/g, '-')}`}
      style={{
        background: '#0A0A0A',
        border: isSemantic
          ? `1px solid ${T}44`
          : `1px solid ${BORDER}`,
        boxShadow: isSemantic ? `0 0 14px ${T}18` : 'none',
      }}
    >
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <p className="text-[11px] font-bold text-white flex items-center gap-1.5 truncate">
          {isSemantic && <Sparkles className="h-3 w-3 flex-shrink-0" style={{ color: T }} />}
          {label}
        </p>
        <p className="text-[10px] tabular-nums font-black flex-shrink-0" style={{ color: accent }}>
          {pct(raw)}
        </p>
      </div>
      {/* progress bar */}
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: '#1A1A1A' }}>
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${pctVal * 100}%`,
            background: isSemantic
              ? `linear-gradient(90deg, ${T}, ${P})`
              : accent,
            boxShadow: isSemantic ? `0 0 8px ${T}66` : 'none',
          }}
        />
      </div>
      <div className="flex items-center justify-between mt-1.5 gap-2">
        <p className="text-[10px] text-gray-500 leading-snug truncate">
          {note || '—'}
        </p>
        {weight != null && score != null && (
          <p className="text-[9px] text-gray-600 flex-shrink-0 tabular-nums">
            +{Number(score).toFixed(1)} pts · w{Number(weight).toFixed(2)}
          </p>
        )}
      </div>
    </div>
  );
}

function ModifierChip({ kind, label, score, note }) {
  const color = kind === 'bonus' ? GREEN : RED;
  const Icon = kind === 'bonus' ? Award : AlertTriangle;
  const sign = (score || 0) >= 0 ? '+' : '';
  return (
    <div
      className="rounded-md px-2.5 py-1.5 flex items-center gap-2"
      style={{ background: '#0A0A0A', border: `1px solid ${color}33` }}
      data-testid={`mx-${kind}-${(label || '').toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon className="h-3 w-3 flex-shrink-0" style={{ color }} />
      <p className="text-[10px] font-bold text-white truncate">{label}</p>
      <span className="text-[10px] tabular-nums font-black flex-shrink-0 ml-auto" style={{ color }}>
        {sign}{Number(score ?? 0).toFixed(1)}
      </span>
      {note && (
        <span className="text-[9px] text-gray-500 truncate hidden md:inline">· {note}</span>
      )}
    </div>
  );
}

// ─────────────── MatchExplainerModal ───────────────

export default function MatchExplainerModal({
  open,
  onOpenChange,
  creator,           // { handle, name, creator_email, match_score }
  onViewFullProfile, // optional callback — switch to CreatorProfileModal
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [explain, setExplain] = useState(null);

  useEffect(() => {
    if (!open || !creator?.creator_email) {
      setExplain(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setExplain(null);
    explainMatch({ creatorEmail: creator.creator_email })
      .then((res) => {
        if (cancelled) return;
        if (res?.ok && res.data) {
          setExplain(res.data);
        } else {
          setError(res?.reason || 'No explanation available');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err.response?.data?.detail || err.message || 'Failed to load match explanation';
        setError(typeof msg === 'string' ? msg : 'Failed to load');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, creator?.creator_email]);

  const score = explain?.dna_score ?? creator?.match_score ?? 0;
  const semanticUsed = !!explain?.semantic_used;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl text-white max-h-[90vh] overflow-y-auto"
        style={{ background: CARD, borderColor: BORDER }}
        data-testid="match-explainer-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Sparkles className="h-5 w-5" style={{ color: T }} />
            Match Breakdown
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            {creator?.handle ? `${creator.handle} — ` : ''}
            why this creator scored {Number(score).toFixed(1)} against your brand
          </DialogDescription>
        </DialogHeader>

        {/* ── Hero score ── */}
        <div
          className="rounded-xl p-4 flex items-center justify-between"
          style={{
            background: 'linear-gradient(135deg, #0F0F0F, #0A0A0A)',
            border: `1px solid ${BORDER}`,
          }}
          data-testid="mx-hero"
        >
          <div className="min-w-0">
            <p className="text-[9px] uppercase tracking-[0.18em] font-black text-gray-500">DNA Score</p>
            <p className="text-3xl font-black tabular-nums mt-0.5" style={{ color: scoreColor((score || 0) / 100) }}>
              {Number(score).toFixed(1)}
              <span className="text-base text-gray-500 font-bold ml-1">/100</span>
            </p>
            {explain?.vertical && (
              <p className="text-[10px] text-gray-500 mt-1 uppercase tracking-wider">
                Vertical · {explain.vertical}
              </p>
            )}
          </div>
          {semanticUsed && (
            <div className="text-right" data-testid="mx-semantic-badge">
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-black tracking-wider uppercase"
                style={{
                  background: `linear-gradient(135deg, ${T}22, ${P}22)`,
                  border: `1px solid ${T}55`,
                  color: T,
                }}
              >
                <Sparkles className="h-2.5 w-2.5" /> Digital DNA
              </span>
              <p className="text-[9px] text-gray-500 mt-1.5 leading-tight">
                Hybrid: <span className="text-white font-bold">{Math.round((explain?.semantic_blend_alpha ?? 0.7) * 100)}%</span> semantic
              </p>
              <p className="text-[9px] text-gray-600 mt-0.5">text-embedding-3-large</p>
            </div>
          )}
        </div>

        {/* ── Loading / Error ── */}
        {loading && (
          <div className="flex flex-col items-center py-10" data-testid="mx-loading">
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: T }} />
            <p className="text-[11px] text-gray-500 mt-3">Computing breakdown…</p>
          </div>
        )}

        {error && !loading && (
          <div
            className="rounded-lg p-4 flex items-start gap-2.5"
            style={{ background: '#1A0F0F', border: `1px solid ${RED}44` }}
            data-testid="mx-error"
          >
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: RED }} />
            <div>
              <p className="text-[12px] font-bold text-white">Match breakdown unavailable</p>
              <p className="text-[11px] text-gray-400 mt-1 leading-relaxed">{error}</p>
              <p className="text-[10px] text-gray-500 mt-2">
                The basic match score from Discover is still valid; this view shows
                the seven-component decomposition once the SSP returns it.
              </p>
            </div>
          </div>
        )}

        {/* ── Components ── */}
        {!loading && !error && explain && (
          <>
            <div className="space-y-2.5" data-testid="mx-components">
              <p className="text-[9px] uppercase tracking-[0.18em] font-black text-gray-500 mt-1">
                Component Breakdown
              </p>
              {(explain.components || []).map((c) => (
                <ComponentBar
                  key={c.label}
                  label={c.label}
                  raw={c.raw}
                  score={c.score}
                  weight={c.weight}
                  note={c.note}
                  isSemantic={c.label === 'Semantic match'}
                />
              ))}
            </div>

            {/* ── Bonuses ── */}
            {(explain.bonuses || []).length > 0 && (
              <div className="space-y-1.5 mt-2" data-testid="mx-bonuses">
                <p className="text-[9px] uppercase tracking-[0.18em] font-black text-gray-500">
                  Bonuses
                </p>
                {explain.bonuses.map((b) => (
                  <ModifierChip
                    key={b.label}
                    kind="bonus"
                    label={b.label}
                    score={b.score}
                    note={b.note}
                  />
                ))}
              </div>
            )}

            {/* ── Penalties ── */}
            {(explain.penalties || []).length > 0 && (
              <div className="space-y-1.5 mt-2" data-testid="mx-penalties">
                <p className="text-[9px] uppercase tracking-[0.18em] font-black text-gray-500">
                  Penalties
                </p>
                {explain.penalties.map((p2) => (
                  <ModifierChip
                    key={p2.label}
                    kind="penalty"
                    label={p2.label}
                    score={p2.score}
                    note={p2.note}
                  />
                ))}
              </div>
            )}

            {/* ── 1st-party data signals (safe surface) ── */}
            <FirstPartyDataPanel
              signals={explain.first_party_signals}
              className="mt-2"
              testId="mx-first-party-panel"
            />

            {/* ── Footer ── */}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#1A1A1A] mt-2">
              {onViewFullProfile && creator?.handle && (
                <button
                  onClick={() => onViewFullProfile(creator.handle)}
                  data-testid="mx-view-profile-btn"
                  className="text-[11px] font-bold flex items-center gap-1 px-3 py-1.5 rounded-md transition-colors hover:bg-white/5"
                  style={{ color: T }}
                >
                  View full creator profile <ChevronRight className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
